import { addDays, format, parseISO } from 'date-fns';
import type { BusinessExpenseQuarterRow, ReportDateRange, ReportTransactionRow } from '../../types/report.types';

/** Australian financial-year quarters (Jul–Jun). */
const AU_FY_QUARTERS = [
  { label: 'Jul–Sep', startMonth: 7, endMonth: 9 },
  { label: 'Oct–Dec', startMonth: 10, endMonth: 12 },
  { label: 'Jan–Mar', startMonth: 1, endMonth: 3 },
  { label: 'Apr–Jun', startMonth: 4, endMonth: 6 },
] as const;

function quarterBoundsInYear(
  year: number,
  startMonth: number,
  endMonth: number
): { start: string; end: string } {
  const startYear = startMonth >= 7 ? year : year + 1;
  const endYear = endMonth >= 7 ? year : year + 1;
  const start = format(new Date(startYear, startMonth - 1, 1), 'yyyy-MM-dd');
  const end = format(new Date(endYear, endMonth, 0), 'yyyy-MM-dd');
  return { start, end };
}

function intersectRange(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): { start: string; end: string } | null {
  const start = aStart > bStart ? aStart : bStart;
  const end = aEnd < bEnd ? aEnd : bEnd;
  if (start > end) return null;
  return { start, end };
}

export function buildBusinessExpenseQuarterTotals(
  period: ReportDateRange,
  transactions: ReportTransactionRow[]
): BusinessExpenseQuarterRow[] {
  const periodStartYear = parseISO(period.startDate).getFullYear();
  const fyAnchorYear =
    parseISO(period.startDate).getMonth() + 1 >= 7 ? periodStartYear : periodStartYear - 1;

  const rows: BusinessExpenseQuarterRow[] = [];

  for (const q of AU_FY_QUARTERS) {
    const bounds = quarterBoundsInYear(fyAnchorYear, q.startMonth, q.endMonth);
    const slice = intersectRange(period.startDate, period.endDate, bounds.start, bounds.end);
    if (!slice) continue;

    const inQuarter = transactions.filter((tx) => tx.date >= slice.start && tx.date <= slice.end);
    const totalAmountAud = inQuarter.reduce(
      (s, tx) => s + (tx.expenseAmountAud ?? Math.abs(tx.amount)),
      0
    );
    const claimableParts = inQuarter
      .map((tx) => tx.claimableAmountAud)
      .filter((v): v is number => v != null);
    rows.push({
      quarterLabel: q.label,
      startDate: slice.start,
      endDate: slice.end,
      transactionCount: inQuarter.length,
      totalAmountAud,
      claimableAmountAud: claimableParts.length
        ? claimableParts.reduce((a, b) => a + b, 0)
        : null,
    });
  }

  return rows.filter((r) => r.transactionCount > 0 || r.totalAmountAud > 0);
}

/** Prior-year period with same inclusive length ending one year earlier. */
export function priorYearPeriod(period: ReportDateRange): ReportDateRange {
  const start = parseISO(period.startDate);
  const end = parseISO(period.endDate);
  const shift = (d: Date) => format(addDays(d, -365), 'yyyy-MM-dd');
  return {
    ...period,
    startDate: shift(start),
    endDate: shift(end),
    label: `Prior year (${shift(start)} – ${shift(end)})`,
  };
}
